import isSameWeek from 'date-fns/isSameWeek';
import type {
	FiltersType,
	SelectValue,
	SelectOption,
	Value,
} from '@atlassian/jira-business-filters';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create';
import { BASE_FIELDS, EMPTY_FILTER_VALUE } from '../../common/constants';
import type {
	BoardIssueComponentFieldOld,
	BoardIssueOld,
	BoardIssueCategoryFieldOld,
	BoardIssueLabelFieldOld,
	BoardIssueUserFieldOld,
	BoardIssueSelectFieldOld,
} from '../../common/types';
import type { Issue as JWMBoardIssue } from '../board-store/types';

export const isAssignedToMe = (issues: BoardIssueOld[], atlassianAccountId: null | string) =>
	issues.filter(({ id, assignee }) => isOptimisticIssue(id) || assignee?.id === atlassianAccountId);

export const isDueThisWeek = (issues: JWMBoardIssue[]) =>
	issues.filter((issue) => {
		if (isOptimisticIssue(issue.id)) {
			return true;
		}
		if (!issue.dueDate) return false;
		const dueDate = new Date(issue.dueDate);
		const today = new Date();
		return isSameWeek(dueDate, today);
	});

// keeping this here in case someone copy pastes JQL in URL
export const isDone = (issues: JWMBoardIssue[]) => issues;

/*
 * Map is used to map filter keys to Issue keys.
 * Only for fields staying on issue. (Not extended fields)
 */
export const FILTER_MAP = {
	assignee: {
		issueKey: ['assignee', 'id'],
		filterValueKey: 'value',
	},
	priority: {
		issueKey: ['priority', 'name'],
		filterValueKey: 'label',
	},
	issuetype: {
		issueKey: 'issueTypeId',
		filterValueKey: 'id',
	},
	type: {
		issueKey: 'issueTypeId',
		filterValueKey: 'id',
	},
	status: {
		issueKey: 'untranslatedStatusName',
		filterValueKey: 'value',
	},
} as const;

// this map is used to call filter functions on issues
export const QUICK_FILTER_MAP = {
	ASSIGNED_TO_ME: isAssignedToMe,
	DONE: isDone,
	DUE_THIS_WEEK: isDueThisWeek,
} as const;

export const filterIssuesByText: (
	arg1: BoardIssueOld[],
	arg2: string | undefined,
) => BoardIssueOld[] = (issues, searchText) => {
	if (searchText !== undefined && searchText.trim() !== '') {
		const trimmedSearchTextInLowerCase = searchText.trim().toLowerCase();
		return issues.filter(
			({ id, summary, assignee, key }) =>
				isOptimisticIssue(id) ||
				summary.toLowerCase().includes(trimmedSearchTextInLowerCase) ||
				assignee?.displayName?.toLowerCase().includes(trimmedSearchTextInLowerCase) ||
				key.toLowerCase().includes(trimmedSearchTextInLowerCase),
		);
	}
	return issues;
};

export const COMPONENT_FILTER = 'component';
export const CATEGORY_FILTER = 'Category[Category]';

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export const filterKeyToIssueFieldKey = (filterKey: string, categoryFieldId: string | void) => {
	if (filterKey === COMPONENT_FILTER) {
		return 'components';
	}

	if (categoryFieldId != null && filterKey === CATEGORY_FILTER) {
		return categoryFieldId;
	}

	return filterKey;
};

export const getFilterValues = (filterValueForExtendedField: SelectValue<SelectOption>) =>
	new Set(filterValueForExtendedField.map((filterValue) => filterValue.value));

const isSelectOption = (filterValue: Value): filterValue is SelectValue<SelectOption> =>
	Array.isArray(filterValue) && filterValue.length > 0 && 'value' in filterValue[0];

export type ExpandedFilter = {
	filterKey: string;
	fieldKey: string;
	filterValues: Set<string | null>;
	hasEmptyFilter: boolean;
};

export const getExpandedFilters = (
	filters: FiltersType,
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	categoryFieldId: string | void,
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	issueDetailFields: string[] | void,
): ExpandedFilter[] =>
	Object.entries(filters)
		.filter(([filterKey]) => !BASE_FIELDS.includes(filterKey))
		.filter(
			([filterKey]) =>
				issueDetailFields != null &&
				issueDetailFields.includes(filterKeyToIssueFieldKey(filterKey, categoryFieldId)),
		)
		.map(([filterKey, filterValue]) => {
			const filterValues = isSelectOption(filterValue)
				? getFilterValues(filterValue)
				: new Set<string>();
			return {
				filterKey,
				fieldKey: filterKeyToIssueFieldKey(filterKey, categoryFieldId),
				filterValues,
				hasEmptyFilter: filterValues.has(EMPTY_FILTER_VALUE),
			};
		});

export const filterByProjectComponents = (
	filter: ExpandedFilter,
	extendedField: BoardIssueComponentFieldOld,
) => {
	const components = extendedField.value;
	return (
		Array.isArray(components) &&
		(components.some((component) => filter.filterValues.has(component)) ||
			(filter.hasEmptyFilter && !components.length))
	);
};

export const filterByCategory = (
	filter: ExpandedFilter,
	extendedField: BoardIssueCategoryFieldOld,
) => {
	const category = extendedField.value;
	return filter.filterValues.has(category ?? '') || (filter.hasEmptyFilter && !category);
};

export const filterByLabels = (filter: ExpandedFilter, extendedField: BoardIssueLabelFieldOld) => {
	const labels = extendedField.value;
	return (
		Array.isArray(labels) &&
		(labels.some((label) => filter.filterValues.has(label)) ||
			(filter.hasEmptyFilter && !labels.length))
	);
};

export const filterByUser = (filter: ExpandedFilter, extendedField: BoardIssueUserFieldOld) => {
	const users = extendedField.users.map((user) => user.accountId);
	return (
		Array.isArray(users) &&
		(users.some((user) => filter.filterValues.has(user)) ||
			(filter.hasEmptyFilter && !users.length))
	);
};

export const filterBySelect = (filter: ExpandedFilter, extendedField: BoardIssueSelectFieldOld) => {
	const selectedValues = extendedField.value;
	return (
		Array.isArray(selectedValues) &&
		(selectedValues.some((selectedValue) => filter.filterValues.has(selectedValue)) ||
			(filter.hasEmptyFilter && !selectedValues.length))
	);
};
